import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Owner-side access invite endpoints — canonical member-facing routes
 * (`/public/me/access-invites*`). Responses use the `{ success, data }`
 * envelope. Invite rows are the raw `AccessInvite` columns.
 */

export interface AccessInvite {
  access_invite_id: string;
  reference?: string;
  location_id?: string | null;
  visitor_name?: string;
  visitor_phone?: string;
  visitor_email?: string;
  status: string;
  /** Visit window — backend columns `valid_from` / `valid_until`. */
  valid_from?: string | null;
  valid_until?: string | null;
  credential_kind?: string;
  accepted_at?: string | null;
  require_visitor_selfie?: boolean;
  biometric_enrolment_status?: string | null;
  visitor_selfie_storage_key?: string | null;
}

export interface AccessInviteComposerSettings {
  default_validity_days: number;
  max_validity_days: number;
}

export interface NewAccessInvitePayload {
  location_id?: string | null;
  access_entry_type_id?: string | null;
  member_contact_id?: string | null;
  visitor_name?: string | null;
  visitor_phone?: string | null;
  visitor_email?: string | null;
  require_visitor_selfie?: boolean;
  pin_lifecycle?: 'one_time' | 'multi_entry';
  valid_until?: string | null;
  public_base_url?: string | null;
}

export interface EligibleLocation {
  location_id: string;
  name?: string;
  label?: string;
  location_code?: string | null;
  address?: string | null;
}

export interface MemberAccessEntryTypeOption {
  access_entry_type_id: string;
  code?: string | null;
  name: string;
  credential_combination_mode?: string | null;
  invite_message_template_id?: string | null;
  require_visitor_selfie_default?: boolean;
  notify_supports_email?: boolean;
  notify_supports_sms?: boolean;
  gate_notify_supports_sms?: boolean;
}

export async function fetchAccessInvites(): Promise<AccessInvite[]> {
  const { data } = await tenantApi.get('/public/me/access-invites');
  return unwrapList<AccessInvite>(data);
}

export async function fetchAccessInviteLocations(): Promise<EligibleLocation[]> {
  const { data } = await tenantApi.get('/public/me/access-invite-locations');
  return unwrapList<EligibleLocation>(data);
}

export async function fetchAccessEntryTypesForInvite(): Promise<MemberAccessEntryTypeOption[]> {
  const { data } = await tenantApi.get('/public/me/access-entry-types');
  return unwrapList<MemberAccessEntryTypeOption>(data);
}

export async function fetchAccessInviteSettings(): Promise<AccessInviteComposerSettings> {
  const { data } = await tenantApi.get('/public/me/access-invite-settings');
  return unwrap<AccessInviteComposerSettings>(data);
}

export async function createAccessInvite(payload: NewAccessInvitePayload): Promise<AccessInvite> {
  const { data } = await tenantApi.post('/public/me/access-invites', payload);
  return unwrap<AccessInvite>(data);
}

export async function cancelAccessInvite(accessInviteId: string): Promise<void> {
  await tenantApi.post(`/public/me/access-invites/${accessInviteId}/cancel`);
}

export async function uploadVisitorSelfie(
  inviteId: string,
  file: File,
  opts?: { public_base_url?: string },
): Promise<AccessInvite> {
  const fd = new FormData();
  fd.append('selfie', file);
  if (opts?.public_base_url) {
    fd.append('public_base_url', opts.public_base_url);
  }
  const { data } = await tenantApi.post(`/public/me/access-invites/${inviteId}/visitor-selfie`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap<AccessInvite>(data);
}
